import React from "react";
import "./GlobalAiToast.css";
import GlobalAiInboxDrawer from "./GlobalAiInboxDrawer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';

const MAX_STACK = 4;              // how many toasts visible at once
const TOAST_TTL_MS = 60_000;      // 1 minute

export function GlobalAiToastProvider({ children }) {
  const [soundOn, setSoundOn] = React.useState(true);

  // visible stacked toasts
  const [toasts, setToasts] = React.useState([]); // [{ id, key, title, message, createdAt, meta }]
  // inbox/history (for bell list)
  const [inbox, setInbox] = React.useState([]);   // newest first
  const [inboxOpen, setInboxOpen] = React.useState(false);
  const [active, setActive] = React.useState(null); // clicked notification -> drawer

  const audioRef = React.useRef(null);
  const dedupeRef = React.useRef(new Set()); // key -> prevent duplicates

  React.useEffect(() => {
    audioRef.current = new Audio("/hype_signal_detected.mp3");
    audioRef.current.preload = "auto";
  }, []);

  const playSound = React.useCallback(() => {
    if (!soundOn) return;
    const a = audioRef.current;
    if (!a) return;
    try {
      a.currentTime = 0;
      a.play().catch(() => {});
    } catch {}
  }, [soundOn]);

  const show = React.useCallback((payload) => {
    if (!payload) return;

    const key =
      payload.key ||
      `${payload.symbol || ""}:${payload.runAt || ""}:${payload.title || ""}:${payload.message || ""}`;

    if (dedupeRef.current.has(key)) return;
    dedupeRef.current.add(key);

    const id = Date.now() + Math.floor(Math.random() * 1000);
    const createdAt = Date.now();

    const item = {
      id,
      key,
      createdAt,
      symbol: payload.symbol || null,
      title: payload.title || "AI update",
      message: payload.message || "",
      ai: payload.ai || null, // optional full ai payload to show in drawer
      meta: payload.meta || {},
      read: false,
    };

    // add to inbox (history)
    setInbox((prev) => [item, ...prev].slice(0, 200));

    // add to visible stack
    setToasts((prev) => {
      const next = [item, ...prev];
      return next.slice(0, MAX_STACK);
    });

    playSound();

    // auto remove from visible stack after TTL (but keep in inbox)
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      // allow repeats later (optional). If you want “never repeat”, remove this line.
      setTimeout(() => dedupeRef.current.delete(key), 2_000);
    }, TOAST_TTL_MS);
  }, [playSound]);

  // Listen to global dispatch events: window.dispatchEvent(new CustomEvent("ai-toast",{detail:{...}}))
  React.useEffect(() => {
    const onToast = (e) => e?.detail && show(e.detail);
    window.addEventListener("ai-toast", onToast);
    return () => window.removeEventListener("ai-toast", onToast);
  }, [show]);

  function markRead(id) {
    setInbox((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }

  const unreadCount = React.useMemo(
    () => inbox.reduce((n, it) => n + (it.read ? 0 : 1), 0),
    [inbox]
  );

  function openFromInbox(item) {
    markRead(item.id);
    setActive(item);
    setInboxOpen(true);
  }

  return (
    <>
      {children}

      {/* 🔔 Notification center button (global) */}
            <button
            className="gai-bell"
            title="AI notifications"
            onClick={() => setInboxOpen((v) => !v)}
            >
            <FontAwesomeIcon
              icon={faEnvelope}
              className={unreadCount > 0 ? "gai-mail-unread" : ""}
            />

            {unreadCount > 0 && (
                <span className="gai-bell-badge">
                {unreadCount}
                </span>
            )}
            </button>


      {/* 📬 Inbox drawer list */}
      <GlobalAiInboxDrawer
        open={inboxOpen}
        onClose={() => setInboxOpen(false)}
        soundOn={soundOn}
        setSoundOn={setSoundOn}
        inbox={inbox}
        onOpenItem={openFromInbox}
        onMarkAllRead={() => setInbox((prev) => prev.map((n) => ({ ...n, read: true })))}
      />

      {/* 🧠 Clicked notification -> AI detail drawer */}
      {active && (
        <div className="gai-detail-overlay" onMouseDown={() => setActive(null)}>
          <div className="gai-detail-panel" onMouseDown={(e) => e.stopPropagation()}>
            <div className="gai-detail-head">
              <div className="gai-detail-title">
                AI Decision — {active.symbol ? active.symbol.replace(/USDT$/i, "") : "Token"}
              </div>
              <button className="gai-x" onClick={() => setActive(null)} aria-label="Close">×</button>
            </div>

            <div className="gai-detail-sub">
              <div className="gai-detail-chip">AI</div>
              <div className="gai-detail-when">{new Date(active.createdAt).toLocaleString()}</div>
            </div>

            <div className="gai-detail-block">
              <div className="gai-detail-h">{active.title}</div>
              <div className="gai-detail-msg">{active.message}</div>
            </div>

            {/* If you pass `ai` payload from backend, show it like HypeAiDrawer */}
            {active.ai && (
              <>
                <div className="gai-detail-block">
                  <div className="gai-detail-h">Entry Plan</div>
                  <div className="gai-detail-msg">{active.ai.lastEntryPlan || "—"}</div>
                </div>
                <div className="gai-detail-block">
                  <div className="gai-detail-h">Risk Plan</div>
                  <div className="gai-detail-msg">{active.ai.lastRiskPlan || "—"}</div>
                </div>
                <div className="gai-detail-block">
                  <div className="gai-detail-h">Why</div>
                  {Array.isArray(active.ai.lastWhy) && active.ai.lastWhy.length ? (
                    <ul className="gai-detail-list">
                      {active.ai.lastWhy.slice(0, 8).map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  ) : (
                    <div className="gai-detail-msg">—</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ✅ Stacked toast UI */}
      <div className="gai-stack" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className="gai-toast">
            <div className="gai-toast-top">
              <div className="gai-badge">AI</div>
              <div className="gai-title">{t.title}</div>
              <button className="gai-x" onClick={() => setToasts((p) => p.filter(x => x.id !== t.id))}>
                ×
              </button>
            </div>
            <div className="gai-msg">
              <span className="gai-caret" />
              {t.message}
            </div>
            <div className="gai-actions">
              <button className="gai-btn" onClick={() => setSoundOn(v => !v)}>
                {soundOn ? "🔔 Sound ON" : "🔕 Sound OFF"}
              </button>
              <button
                className="gai-btn primary"
                onClick={() => openFromInbox(t)}
                title="Open details"
              >
                Open
              </button>
            </div>
            <div className="gai-glow" />
          </div>
        ))}
      </div>
    </>
  );
}
